import { inject, Injectable } from '@angular/core';
import { FileSystemSharing } from '../models/file-sharing.models';
import { API_CONFIG, ApiCallingV2Service } from '@zayan-erp/shared';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class FilesystemService {
  #http = inject(ApiCallingV2Service);
  #apiConfig = inject(API_CONFIG);
  baseUrl = this.#apiConfig.baseUrlV2;

  // Directory listing
  listDirectory(folderId?: string | null): Observable<any> {
    const url = folderId
      ? `${this.baseUrl}/filesystem/${folderId}?foldersOnly=false`
      : `${this.baseUrl}/filesystem?foldersOnly=false`;
    return this.#http.getData<any>(url, { showLoader: false, showMessage: false });
  }

  listFoldersOnly(folderId?: string | null): Observable<any> {
    const url = folderId
      ? `${this.baseUrl}/filesystem/${folderId}?foldersOnly=true`
      : `${this.baseUrl}/filesystem?foldersOnly=true`;
    return this.#http.getData<any>(url, { showLoader: true, showMessage: false });
  }

  listDirectoryPublic(token: string, subFileSystemId: string): Observable<any> {
    return this.#http.getData<any>(`${this.baseUrl}/public/filesystem/${token}?subFileSystemId=${subFileSystemId}`, {
      showLoader: true,
      showMessage: false
    });
  }

  // Folder / template creation
  createFolderSilent(payload: {
    name: string | null | undefined;
    parentId: string | null;
    notes: string | null;
  }): Observable<any> {
    return this.#http.postData<any>(
      `${this.baseUrl}/filesystem/folders`,
      { showLoader: true, showMessage: false },
      payload
    );
  }

  createFolderFromTemplate(payload: {
    name: string | null | undefined;
    templateId: string | null | undefined;
  }): Observable<any> {
    return this.#http.postData<any>(
      `${this.baseUrl}/filesystem/folders/from-template`,
      { showLoader: true, showMessage: false },
      payload
    );
  }

  // Rename / notes
  renameFile(
    id: string,
    payload: { name: string; parentId: string | null; notes: string | null },
    showLoader = true
  ): Observable<any> {
    return this.#http.putData<any>(`${this.baseUrl}/filesystem/${id}/rename`, payload, {
      showLoader,
      showMessage: true
    });
  }

  updateNotes(id: string, notes: string, showLoader = true): Observable<any> {
    return this.#http.putData<any>(
      `${this.baseUrl}/filesystem/${id}/updateNotes`,
      { notes },
      { showLoader, showMessage: true }
    );
  }

  // Delete
  bulkDelete(ids: string[], showLoader = true): Observable<any> {
    return this.#http.postData<any>(`${this.baseUrl}/filesystem/bulk-delete`, { showLoader, showMessage: true }, ids);
  }

  // Move
  moveFile(id: string, destinationFolderId: string | null, showLoader = true): Observable<any> {
    return this.#http.putData<any>(
      `${this.baseUrl}/filesystem/${id}/move`,
      { destinationFolderId },
      { showLoader, showMessage: true, message: 'Item moved successfully' }
    );
  }

  bulkMove(ids: string[], destinationFolderId: string | null, showLoader = true): Observable<any> {
    return this.#http.postData<any>(
      `${this.baseUrl}/filesystem/bulk-move`,
      { showLoader, showMessage: true, message: 'Items moved successfully' },
      { ids, destinationFolderId }
    );
  }

  createBulkDownloadJob(fileIds: string[]): Observable<any> {
    return this.#http.postData<any>(
      `${this.baseUrl}/filesystem/bulk-download`,
      { showLoader: true, showMessage: false },
      { fileIds: fileIds }
    );
  }

  // Trees
  getFolderTree(id?: string | null): Observable<any> {
    return this.#http.getData<any>(`${this.baseUrl}/filesystem/tree?id=${id ?? ''}`, {
      showLoader: false,
      showMessage: false
    });
  }

  getTemplateTree(id?: string | null): Observable<any> {
    return this.#http.getData<any>(`${this.baseUrl}/filesystem/treeTemplate?id=${id ?? ''}`, {
      showLoader: false,
      showMessage: false
    });
  }

  // Permissions
  getPermissions(id: string): Observable<any> {
    return this.#http.getData<any>(`${this.baseUrl}/filesystem/${id}/permissions`, {
      showLoader: true,
      showMessage: false
    });
  }

  addPermissions(id: string, payload: { userIds: string[]; groupIds: string[] }): Observable<any> {
    return this.#http.postData<any>(
      `${this.baseUrl}/filesystem/${id}/permissions/add`,
      { showLoader: true, showMessage: true },
      payload
    );
  }

  removeUserPermission(permissionId: string): Observable<any> {
    return this.#http.deleteData<any>(`${this.baseUrl}/filesystem/permissions/${permissionId}`, {
      showLoader: true,
      showMessage: true
    });
  }

  removeGroupPermission(folderId: string, groupId: string): Observable<any> {
    return this.#http.deleteData<any>(`${this.baseUrl}/filesystem/${folderId}/permissions/${groupId}`, {
      showLoader: true,
      showMessage: true
    });
  }

  // File URLs
  getFileDownloadUrl(id: string, download: boolean = false): Observable<any> {
    return this.#http.getData<any>(`${this.baseUrl}/filesystem/${id}/fileUrl?download=${download}`, {
      showLoader: false,
      showMessage: false
    });
  }

  // Link sharing (silent variant - used inline by tables/all-files; toast variant lives in CopyLinkService)
  getLinkToken(id: string): Observable<any> {
    return this.#http.getData<any>(`${this.baseUrl}/filesystem/${id}/link`, {
      showLoader: true,
      showMessage: false
    });
  }

  toggleLinkRestriction(id: string): Observable<any> {
    return this.#http.putData<any>(`${this.baseUrl}/filesystem/${id}/link/toggle`, '', {
      showLoader: true,
      showMessage: false
    });
  }

  // Shared links lists (kept for existing callers)
  getSharedFolders(pageNumber: number, pageSize: number): Observable<any[]> {
    return this.#http.getData<any[]>(
      `${this.baseUrl}/filesystem/folders/links?PageNumber=${pageNumber}&PageSize=${pageSize}`,
      { showLoader: true, showMessage: false }
    );
  }

  getSharedFiles(pageNumber: number, pageSize: number): Observable<any[]> {
    return this.#http.getData<any[]>(
      `${this.baseUrl}/filesystem/files/links?PageNumber=${pageNumber}&PageSize=${pageSize}`,
      { showLoader: true, showMessage: false }
    );
  }

  getSharedWith(id: string): Observable<FileSystemSharing> {
    return this.#http.getData<FileSystemSharing>(`${this.baseUrl}/filesystem/${id}/permissions`, {
      showLoader: true,
      showMessage: false
    });
  }
}
